import { useEffect } from "react";

/**
 * WS-11 Correction Round — canonical URL hook.
 *
 * Emits exactly one <link rel="canonical" href="https://stockaily.ai${path}">
 * into the document head while the calling component is mounted.
 *
 * Rules enforced here (see WS-11 correction-round spec):
 *   * Canonical origin is exactly "https://stockaily.ai" — no locale
 *     prefix before /de/ or /en/, no trailing slash added by the hook.
 *   * The `path` argument MUST already be the canonical path
 *     (e.g. "/de/impressum", "/en/privacy", "/en/withdrawal-form").
 *     If a caller ever passes a "/legal/*" path the hook refuses to
 *     emit anything, so legacy routes cannot accidentally become
 *     canonical destinations.
 *   * Exactly one canonical <link> per page: on mount the hook removes
 *     any prior canonical link that was left over from a previous
 *     mount before inserting its own, and on unmount / path change it
 *     removes its own.
 *   * No dependency on react-helmet — direct DOM insertion scoped to
 *     the mount lifecycle.
 */
const CANONICAL_ORIGIN = "https://stockaily.ai";
const CANONICAL_MARKER = "data-canonical-managed";

export function useCanonicalUrl(path) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    // Defensive: never emit a canonical for a legacy /legal/* path.
    if (!path || typeof path !== "string" || path.startsWith("/legal/")) {
      return undefined;
    }

    // Remove any stale canonical <link> — both managed and any accidental
    // pre-existing one — so we guarantee exactly one canonical per page.
    const stale = document.head.querySelectorAll('link[rel="canonical"]');
    stale.forEach((el) => {
      try {
        document.head.removeChild(el);
      } catch (_e) {
        /* already detached */
      }
    });

    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", `${CANONICAL_ORIGIN}${path}`);
    link.setAttribute(CANONICAL_MARKER, "true");
    document.head.appendChild(link);

    return () => {
      try {
        document.head.removeChild(link);
      } catch (_e) {
        /* already removed */
      }
    };
  }, [path]);
}

export default useCanonicalUrl;
